import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { CostRecipe, DimensionPricing } from "@workspace/api-zod/cost-recipe";

export type Measure = DimensionPricing["measure"];
export const MEASURES: Measure[] = ["length", "area", "volume", "weight", "count"];
export const MEASURE_UNIT: Record<Measure, string> = { length: "m", area: "m²", volume: "m³", weight: "kg", count: "each" };

/** Editable string draft of a dimension pricing model. Extra fields (reference_price, actual_quantity) are preserved. */
export interface DimensionPricingDraft {
  measure: Measure;
  reference_quantity: string;
  dimension_labels: string;
  quantity_basis?: DimensionPricing["quantity_basis"];
  reference_dimensions?: DimensionPricing["reference_dimensions"];
  fixed_cost: string;
  evidence: string;
  reference_price?: number;
  actual_quantity?: number;
  resolved_quantity?: number;
  resolved_spec?: string;
  section_rates?: Array<Omit<NonNullable<DimensionPricing["section_rates"]>[number], "rate_per_metre"> & { rate_per_metre: string }>;
  length_label?: string;
  selected_section?: string;
  resolved_section?: string;
}

export function dimensionDraftFrom(m?: DimensionPricing | null): DimensionPricingDraft | undefined {
  if (!m) return undefined;
  return {
    measure: m.measure,
    reference_quantity: String(m.reference_quantity),
    dimension_labels: m.dimension_labels.join(", "),
    ...(m.quantity_basis ? { quantity_basis: m.quantity_basis } : {}),
    ...(m.reference_dimensions ? { reference_dimensions: m.reference_dimensions.map(d => ({ ...d })) } : {}),
    fixed_cost: String(m.fixed_cost ?? 0),
    evidence: m.evidence,
    ...(m.reference_price != null ? { reference_price: m.reference_price } : {}),
    ...(m.actual_quantity != null ? { actual_quantity: m.actual_quantity } : {}),
    ...(m.resolved_quantity != null ? { resolved_quantity: m.resolved_quantity } : {}),
    ...(m.resolved_spec != null ? { resolved_spec: m.resolved_spec } : {}),
    ...(m.section_rates ? { section_rates: m.section_rates.map(r => ({ ...r, rate_per_metre: String(r.rate_per_metre) })) } : {}),
    ...(m.length_label ? { length_label: m.length_label } : {}),
    ...(m.selected_section ? { selected_section: m.selected_section } : {}),
    ...(m.resolved_section ? { resolved_section: m.resolved_section } : {}),
  };
}

export function emptyDimensionDraft(): DimensionPricingDraft {
  return { measure: "area", reference_quantity: "", dimension_labels: "width, height", fixed_cost: "0", evidence: "" };
}

const n = (v: string) => (v.trim() === "" ? NaN : Number(v.trim()));

export function dimensionDraftToInput(d: DimensionPricingDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {
    measure: d.measure,
    reference_quantity: n(d.reference_quantity),
    dimension_labels: d.dimension_labels.split(",").map(s => s.trim()).filter(Boolean),
    fixed_cost: d.fixed_cost.trim() === "" ? 0 : n(d.fixed_cost),
    evidence: d.evidence,
    ...(d.quantity_basis ? { quantity_basis: d.quantity_basis } : {}),
  };
  if (d.reference_price != null) out.reference_price = d.reference_price;
  if (d.reference_dimensions) out.reference_dimensions = d.reference_dimensions;
  if (d.resolved_quantity != null) out.resolved_quantity = d.resolved_quantity;
  if (d.resolved_spec != null) out.resolved_spec = d.resolved_spec;
  if (d.actual_quantity != null) out.actual_quantity = d.actual_quantity;
  if (d.section_rates) out.section_rates = d.section_rates.map(r => ({ ...r, rate_per_metre: n(r.rate_per_metre) }));
  if (d.length_label) out.length_label = d.length_label;
  if (d.selected_section) out.selected_section = d.selected_section;
  if (d.resolved_section) out.resolved_section = d.resolved_section;
  return out;
}

function Err({ msg, id }: { msg?: string; id: string }) {
  return msg ? <p className="text-xs text-destructive mt-1" data-testid={`error-${id}`}>{msg}</p> : null;
}

/** Library-side configuration for measured pricing on one material or the specification supply. */
export function DimensionPricingConfig({
  id, value, onChange, errors = {}, errorPrefix, itemLabel, quantityUnit,
}: {
  id: string;
  value: DimensionPricingDraft | undefined;
  onChange: (next: DimensionPricingDraft | undefined) => void;
  errors?: Record<string, string>;
  errorPrefix: string;
  itemLabel: string;
  quantityUnit?: string;
}) {
  const enabled = !!value;
  const e = (k: string) => errors[`${errorPrefix}.${k}`];
  return (
    <div className="rounded-md border border-dashed p-2.5 space-y-2" data-testid={`config-dimension-${id}`}>
      <label className="flex items-start gap-2 text-xs cursor-pointer">
        <input
          type="checkbox" className="mt-0.5" checked={enabled}
          onChange={ev => onChange(ev.target.checked ? emptyDimensionDraft() : undefined)}
          data-testid={`checkbox-dimension-${id}`}
        />
        <span>
          <span className="font-medium">{quantityUnit ? "Calculate quantity of" : "Price"} {itemLabel} by measurement on quotes</span>
          <span className="block text-muted-foreground">Optional. Changes only the variable {quantityUnit ? "consumption; the merchant unit price stays unchanged" : "portion; the fixed portion stays unchanged"}.</span>
        </span>
      </label>
      {value && (
        <div className="space-y-2">
          {value.section_rates && <SectionRatesEditor id={id} value={value} onChange={onChange} />}
          {!value.section_rates && <Label className="text-xs block">Measurement basis
            <select className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={value.quantity_basis ?? "per_item"}
              onChange={e => onChange({ ...value, quantity_basis: e.target.value as DimensionPricing["quantity_basis"] })}>
              <option value="per_item">Per item — count stays separate from size</option>
              <option value="per_work_unit">Per work unit — divide measurements by line quantity</option>
            </select>
          </Label>}
          <div className={`grid gap-2 grid-cols-2 sm:grid-cols-4${value.section_rates ? " hidden" : ""}`}>
            <div>
              <Label className="text-xs" htmlFor={`dm-measure-${id}`}>Measure</Label>
              <select
                id={`dm-measure-${id}`}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={value.measure}
                onChange={ev => onChange({ ...value, measure: ev.target.value as Measure })}
                data-testid={`select-dimension-measure-${id}`}
              >
                {MEASURES.map(m => <option key={m} value={m}>{m} ({MEASURE_UNIT[m]})</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs" htmlFor={`dm-ref-${id}`}>Reference size ({MEASURE_UNIT[value.measure]})</Label>
              <Input id={`dm-ref-${id}`} inputMode="decimal" value={value.reference_quantity}
                onChange={ev => onChange({ ...value, reference_quantity: ev.target.value })}
                data-testid={`input-dimension-reference-${id}`} />
              <Err id={`dimension-reference-${id}`} msg={e("reference_quantity")} />
            </div>
            <div>
              <Label className="text-xs" htmlFor={`dm-fixed-${id}`}>Fixed portion ({quantityUnit ?? "GBP"})</Label>
              <Input id={`dm-fixed-${id}`} inputMode="decimal" value={value.fixed_cost}
                onChange={ev => onChange({ ...value, fixed_cost: ev.target.value })}
                data-testid={`input-dimension-fixed-${id}`} />
              <Err id={`dimension-fixed-${id}`} msg={e(quantityUnit ? "fixed_quantity" : "fixed_cost")} />
            </div>
            <div>
              <Label className="text-xs" htmlFor={`dm-labels-${id}`}>Dimensions (comma-separated)</Label>
              <Input id={`dm-labels-${id}`} value={value.dimension_labels} placeholder="width, height"
                onChange={ev => onChange({ ...value, dimension_labels: ev.target.value })}
                data-testid={`input-dimension-labels-${id}`} />
              <Err id={`dimension-labels-${id}`} msg={e("dimension_labels")} />
              {value.measure === "weight" && <p className="text-xs text-muted-foreground">Use one kg measurement, or two labels for length (m) × section weight (kg/m).</p>}
            </div>
          </div>
          {value.reference_dimensions && <div className="space-y-1 text-xs" data-testid={`reference-axes-${id}`}>
            <p className="text-muted-foreground">Library reference axes — missing quote measurements use these as visible, editable assumptions.</p>
            {value.reference_dimensions.map((axis, i) => <div className="flex items-center gap-2" key={`${axis.label}-${i}`}>
              <Label className="min-w-24" htmlFor={`dm-axis-${id}-${i}`}>{axis.label} ({axis.unit})</Label>
              <Input id={`dm-axis-${id}-${i}`} className="h-8 max-w-32" type="number" min="0.0001" step="any"
                value={axis.value} onChange={ev => {
                  const reference_dimensions = value.reference_dimensions!.map((d, j) => j === i ? { ...d, value: Number(ev.target.value) } : d);
                  const product = reference_dimensions.reduce((n, d) => n * d.value, 1);
                  onChange({ ...value, reference_dimensions, reference_quantity: String(Math.round(product * 10000) / 10000) });
                }} />
            </div>)}
            <Err id={`reference-axes-${id}`} msg={e("reference_dimensions")} />
          </div>}
          <div>
            <Label className="text-xs" htmlFor={`dm-ev-${id}`}>Evidence or assumption</Label>
            <Textarea id={`dm-ev-${id}`} rows={2} value={value.evidence}
              placeholder="Library price assumes a 1.44 m² window; frame and hardware treated as fixed."
              onChange={ev => onChange({ ...value, evidence: ev.target.value })}
              data-testid={`input-dimension-evidence-${id}`} />
            <Err id={`dimension-evidence-${id}`} msg={e("evidence")} />
          </div>
          <p className="text-xs text-muted-foreground" data-testid={`text-dimension-formula-${id}`}>
            {quantityUnit ? "Consumption = fixed quantity + (reference consumption - fixed quantity) × actual / reference. Waste is added once; the merchant unit price does not change." : value.section_rates
              ? "Supply = approved section rate per metre × actual cut length. The included baseline allowance above is replaced once; it is not a per-metre price. Unlisted sections need an approved rate."
              : "Quote price = fixed + (reference price - fixed) x actual / reference. The reference price is the current library price above."}
            {" "}This only affects future quotes; the library base rates are not changed.
          </p>
        </div>
      )}
    </div>
  );
}

function SectionRatesEditor({ id, value, onChange }: {
  id: string; value: DimensionPricingDraft; onChange: (value: DimensionPricingDraft) => void;
}) {
  const rows = value.section_rates!;
  return <details className="rounded border p-2 text-xs" data-testid={`section-rates-${id}`}>
    <summary className="cursor-pointer font-medium">Section supply rates · £/m excluding VAT</summary>
    <p className="mt-2 text-muted-foreground">Editable supplier references, not delivered quotations. Confirm grade, finish and extras.</p>
    <Label className="text-xs">Length dimension label
      <Input aria-label="Section length dimension label" value={value.length_label ?? ""}
        onChange={e => onChange({ ...value, length_label: e.target.value })} />
    </Label>
    {rows.map((row, i) => <div key={i} className="my-2 rounded border p-2 grid gap-2 grid-cols-2">
      {(["section", "rate_per_metre", "grade", "finish", "supplier", "source_url", "checked_on"] as const).map(field =>
        <Label key={field} className={`text-xs${field === "source_url" ? " col-span-2" : ""}`}>
          {({ section: "Section", rate_per_metre: "£/m ex VAT", grade: "Grade", finish: "Finish", supplier: "Supplier", source_url: "Source URL", checked_on: "Checked on (YYYY-MM-DD)" })[field]}
          <Input aria-label={`${field} ${i + 1}`} value={row[field]}
            onChange={e => onChange({ ...value, section_rates: rows.map((r, j) => j === i ? { ...r, [field]: e.target.value } : r) })} />
        </Label>)}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange({ ...value, section_rates: rows.filter((_, j) => j !== i) })}>Remove section</Button>
    </div>)}
    <Button type="button" size="sm" variant="outline" onClick={() => onChange({ ...value, section_rates: [...rows, {
      section: "", rate_per_metre: "", grade: "S355", finish: "mill finish", supplier: "", source_url: "", checked_on: "",
    }] })}>Add section rate</Button>
  </details>;
}

interface Target { key: string; name: string; model: DimensionPricing; materialIndex: number | null }

function targets(recipe: CostRecipe): Target[] {
  const out: Target[] = [];
  if (recipe.shape === "specification" && recipe.supply_pricing)
    out.push({ key: "supply", name: recipe.supply_name ?? "Supply", model: recipe.supply_pricing, materialIndex: null });
  recipe.materials.forEach((m, i) => {
    if (m.dimension_pricing) out.push({ key: m.material_id, name: m.name, model: m.dimension_pricing, materialIndex: i });
  });
  return out;
}

function withActual(recipe: CostRecipe, t: Target, actual: number | undefined): CostRecipe {
  const model = { ...t.model };
  if (actual == null) delete model.actual_quantity; else model.actual_quantity = actual;
  if (t.materialIndex == null && recipe.shape === "specification") return { ...recipe, supply_pricing: model };
  return { ...recipe, materials: recipe.materials.map((m, i) => (i === t.materialIndex ? { ...m, dimension_pricing: model } : m)) };
}

const STATUS_TEXT = {
  calculated: "Calculated breakdown",
  needs_measurements: "Needs measurements",
  manual: "Manual prices",
} as const;

/** Quote-local measurement overrides and pricing explanation. Returns the raw recipe; caller re-prices. */
export function QuoteMaterialPricing({ recipe, onChange }: { recipe: CostRecipe; onChange: (recipe: CostRecipe) => void }) {
  const list = targets(recipe);
  const qp = recipe.quote_pricing;
  if (list.length === 0 && !qp) return null;
  const manual = qp?.status === "manual";
  return (
    <div className="rounded-md border bg-muted/30 p-2.5 space-y-2 text-xs min-w-0" data-testid="panel-quote-material-pricing">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium uppercase tracking-wide text-muted-foreground">
          {recipe.labour_pricing || recipe.plant_pricing || recipe.materials.some(m => m.quantity_pricing) ? "Dimension pricing" : "Material pricing"}
        </span>
        {qp && (
          <span
            className={`rounded px-1.5 py-0.5 font-medium ${qp.status === "calculated" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" : qp.status === "manual" ? "bg-muted text-foreground" : "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-300"}`}
            data-testid="status-quote-pricing"
          >
            {qp.status === "needs_measurements" && list.some(t => t.model.section_rates) ? "Needs section or length" : STATUS_TEXT[qp.status]}
          </span>
        )}
      </div>
      {manual && (
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid="text-quote-pricing-manual">
          <span className="text-muted-foreground">Manual mode: prices on this line were edited by hand and are not recalculated from measurements.</span>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => onChange({ ...recipe, quote_pricing: { ...qp!, status: "calculated" } })}
            data-testid="button-resume-measurement-pricing">
            Resume measurement pricing
          </Button>
        </div>
      )}
      {list.map(t => {
        const sectionRates = t.model.section_rates;
        const unit = sectionRates ? "m" : MEASURE_UNIT[t.model.measure];
        return (
          <div key={t.key} className="space-y-1" data-testid={`row-quote-measure-${t.key}`}>
            {sectionRates && <Label className="text-xs">Steel section for this quote
              <select className="block h-8 w-full rounded border bg-background px-2" aria-label="Steel section for this quote"
                disabled={manual} value={t.model.selected_section ?? ""}
                onChange={e => {
                  const model = { ...t.model };
                  if (e.target.value) model.selected_section = e.target.value; else delete model.selected_section;
                  onChange(t.materialIndex == null && recipe.shape === "specification" ? { ...recipe, supply_pricing: model }
                    : { ...recipe, materials: recipe.materials.map((m, i) => i === t.materialIndex ? { ...m, dimension_pricing: model } : m) });
                }}>
                <option value="">From item specification{t.model.resolved_section ? `: ${t.model.resolved_section}` : " — confirm section"}</option>
                {sectionRates.map(r => <option key={r.section} value={r.section}>{r.section} · {r.grade} · £{r.rate_per_metre.toFixed(2)}/m ex VAT</option>)}
              </select>
            </Label>}
            <Label className="text-xs" htmlFor={`qm-${t.key}`}>
              {t.name}: {sectionRates ? "cut length" : `measured ${t.model.measure}`} for this quote only ({unit})
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id={`qm-${t.key}`} inputMode="decimal" className="h-8 max-w-[10rem]" disabled={manual}
                defaultValue={t.model.actual_quantity != null ? String(t.model.actual_quantity) : ""}
                key={`${t.key}-${t.model.actual_quantity ?? ""}`}
                placeholder={sectionRates ? t.model.length_label : t.model.dimension_labels.join(" x ")}
                onBlur={ev => {
                  ev.target.setCustomValidity("");
                  const raw = ev.target.value.trim();
                  if (raw === "") { if (t.model.actual_quantity != null) onChange(withActual(recipe, t, undefined)); return; }
                  const v = Number(raw);
                  if (!Number.isFinite(v) || v < 0 || v > 99_999_999.99) {
                    ev.target.setCustomValidity("Enter a measurement from 0 to 99,999,999.99");
                    ev.target.reportValidity();
                    return;
                  }
                  if (v === t.model.actual_quantity) return;
                  onChange(withActual(recipe, t, Math.round(v * 10000) / 10000));
                }}
                data-testid={`input-quote-actual-${t.key}`}
              />
              <span className="text-muted-foreground">{unit}</span>
            </div>
            <p className="text-muted-foreground">
              {sectionRates ? <>Blank uses {t.model.length_label}. Supply = section £/m × cut length. Included baseline allowance
                {t.model.reference_price != null ? ` £${t.model.reference_price.toFixed(2)}` : ""} is replaced once, not added again. Confirm delivery, fabrication and finish separately.</> : <>
              Blank uses the line dimensions ({t.model.dimension_labels.join(", ")}). Reference {t.model.reference_quantity} {unit}
              {t.model.reference_price != null ? ` at £${t.model.reference_price.toFixed(2)}` : ""}, fixed £{t.model.fixed_cost.toFixed(2)}.
              Estimate from the library reference, not supplier-verified.</>}
            </p>
          </div>
        );
      })}
      {qp && qp.notes.length > 0 && (
        <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground break-words" data-testid="list-quote-pricing-notes">
          {qp.notes.map((note, i) => <li key={i} data-testid={`text-quote-pricing-note-${i}`}>{note}</li>)}
        </ul>
      )}
    </div>
  );
}
